import { useEffect, useRef, useState } from "react"
import {
  Widget,
  addResponseMessage,
  toggleMsgLoader,
  toggleInputDisabled,
  deleteMessages,
  markAllAsRead,
  setInputText,
  getInputText,
  useIsWidgetOpen,
} from "./widget"
import "./index.scss"
import logo from "utils/stylesheets/logo/logo.png"
import { FaMicrophone, FaRobot } from "react-icons/fa"
import { FaStop } from "react-icons/fa6"
import { useDispatch, useSelector } from "react-redux"
import { chatBot, librarian, message } from "state_management"
import { analyzeAudioBlob } from "utils/scripts/analyseAudio"
import { Dropdown } from "react-bootstrap"
import { TbSquareLetterT, TbSquareLetterG, TbSquareLetterC } from "react-icons/tb"
import useT from "utils/hooks/useT"

export default function ChatBot() {
  const dispatch = useDispatch()
  const { t } = useT("common")
  const { t: tm } = useT("messages")

  const userName = useSelector((state) => state.auth.memberId)
  const status = useSelector((state) => state.chatBot.status)
  const chatbots = useSelector((state) => state.librarian.chatbots)
  const selectedChatbot = useSelector((state) => state.chatBot.selectedChatbot)
  const defaultChatbot = useSelector((state) => state.auth.defaultChatbot)
  const activeConversationId = useSelector((state) => state.chatBot.conversationId)
  const isAdmin = useSelector((state) => state.auth.role === "admin" || state.auth.role === "customer_admin")

  const [isRecording, setIsRecording] = useState(false)
  const [mediaRecorder, setMediaRecorder] = useState(null)
  const [insertionLength, setInsertionLength] = useState(0)
  const cursorPositionRef = useRef(0)
  const [textInserted, setTextInserted] = useState(false)

  const isWidgetVisible = useIsWidgetOpen()

  useEffect(() => {
    if (isAdmin) {
      dispatch(librarian.fetchAdminChatbots()).then((res) => {
        dispatch(chatBot.setChatbot(res.find((chatbot) => chatbot.name === "PlannerTech Help")))
      })
    } else {
      dispatch(librarian.fetchChatbots()).then((res) => {
        dispatch(chatBot.setChatbot(res.find((chatbot) => chatbot.name === "PlannerTech Help")))
      })
    }
  }, [dispatch, defaultChatbot, isAdmin])

  useEffect(() => {
    markAllAsRead()
    deleteMessages()
    addResponseMessage(t("chatbotGreeting"))
  }, [userName, t])

  const handleRefresh = () => {
    dispatch(chatBot.refreshChat())
    deleteMessages()
    addResponseMessage(t("chatbotGreeting"))
  }

  const SpeechToTextButton = ({ onClick }) => (
    <button type="button" className="rcw-stt-button" onClick={onClick} aria-label="Speech to text">
      {isRecording ? <FaStop /> : <FaMicrophone />}
    </button>
  )

  useEffect(() => {
    if (status === "finished") {
      toggleMsgLoader(false)
      toggleInputDisabled(false)
    }
  }, [status])

  // Track cursor position in the textarea so speech-to-text can insert at the caret.
  useEffect(() => {
    if (!isWidgetVisible) return

    const updateCursorPosition = (e) => {
      cursorPositionRef.current = e.target.selectionStart ?? 0
    }

    const timeoutId = setTimeout(() => {
      const input = document.querySelector(".rcw-input")
      if (input) {
        input.addEventListener("input", updateCursorPosition)
        input.addEventListener("click", updateCursorPosition)
        input.addEventListener("keyup", updateCursorPosition)
      }
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      const input = document.querySelector(".rcw-input")
      if (input) {
        input.removeEventListener("input", updateCursorPosition)
        input.removeEventListener("click", updateCursorPosition)
        input.removeEventListener("keyup", updateCursorPosition)
      }
    }
  }, [isWidgetVisible])

  // Highlight the text inserted by speech-to-text
  useEffect(() => {
    if (insertionLength > 0 && textInserted) {
      const input = document.querySelector(".rcw-input")
      if (input) {
        input.focus()
        const startPos = cursorPositionRef.current - insertionLength
        const endPos = cursorPositionRef.current
        input.setSelectionRange(startPos, endPos)
        setTextInserted(false)
      }
    }
  }, [insertionLength, textInserted])

  const handleNewUserMessage = (message, files = []) => {
    toggleMsgLoader(true)
    toggleInputDisabled(true)

    const processResponse = (res) => {
      let fullMessage = res.message

      if (res.links && res.links.length > 0) {
        fullMessage += `\n \n${t("references")}: `
        const linksFormatted = res.links
          .map((link) => `\n[${link.docId}] - [${link.tag}](${link.url})`)
          .join("")
        fullMessage += linksFormatted
      }

      addResponseMessage(fullMessage)
    }

    if (activeConversationId === "") {
      dispatch(chatBot.getAnswer({ isBot: false, message: message }, selectedChatbot.id, undefined, files)).then(processResponse)
    } else {
      dispatch(chatBot.getAnswer({ isBot: false, message: message }, selectedChatbot.id, activeConversationId, files)).then(
        processResponse
      )
    }
  }

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      setMediaRecorder(recorder)

      let audioChunks = []

      recorder.addEventListener("dataavailable", (event) => {
        audioChunks.push(event.data)
      })

      recorder.addEventListener("stop", () => {
        const audioBlob = new Blob(audioChunks, { type: "audio/wav" })
        analyzeAudioBlob(audioBlob).then((isSpeechDetected) => {
          if (isSpeechDetected) {
            dispatch(librarian.getSpeechToTextQuestion(audioBlob)).then((res) => {
              const newText = res.text.trim()
              const currentText = getInputText()
              const beforeCursor = currentText.substring(0, cursorPositionRef.current)
              const afterCursor = currentText.substring(cursorPositionRef.current)
              const updated = `${beforeCursor}${newText}${afterCursor}`
              setInputText(updated)

              cursorPositionRef.current = beforeCursor.length + newText.length
              setInsertionLength(newText.length)
              setTextInserted(true)
            })
          } else {
            return dispatch(message.warning(tm("noSoundDetected")))
          }
        })
        audioChunks = []
      })

      recorder.start(500)
      setIsRecording(true)
    } catch (error) {
      return dispatch(message.warning(tm("noMicrophoneDetected")))
    }
  }

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop()
      mediaRecorder.stream.getTracks().forEach((track) => track.stop())
      setIsRecording(false)
    }
  }

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }

  const headerToolbar = (
    <>
      <button className="rcw-refresh-button" title={t("clearConversation")} onClick={handleRefresh}>
        {t("clear")}
      </button>
      <div className="chatbot-dropdown">
        <Dropdown
        drop="down"
        align="end"
        onSelect={(eventKey) => {
          if (eventKey === "none") {
            dispatch(chatBot.setChatbot({ id: "", name: "" }))
          } else {
            dispatch(chatBot.setChatbot(chatbots.find((chatbot) => chatbot.id === eventKey)))
          }
        }}
      >
        <Dropdown.Toggle variant="link" className="rcw-chatbot-button-dropdown-toggle">
          <FaRobot size={20} />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {chatbots.length > 0 &&
            chatbots.map((chatbot) => (
              <Dropdown.Item key={chatbot.id} eventKey={chatbot.id}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "250px",
                      marginRight: "10px",
                    }}
                    title={chatbot.name}
                  >
                    {chatbot.name}
                  </span>
                  <div style={{ flexShrink: 0 }}>
                    {chatbot.uniqueForTeam ? (
                      <TbSquareLetterT size={20} title={t("teamChatbot")} />
                    ) : chatbot.isGlobal ? (
                      <TbSquareLetterG size={20} title={t("globalChatbot")} />
                    ) : (
                      <TbSquareLetterC size={20} title={t("companyWideChatbot")} />
                    )}
                  </div>
                </div>
              </Dropdown.Item>
            ))}
        </Dropdown.Menu>
      </Dropdown>
      </div>
    </>
  )

  return (
    <Widget
      title={t("qcLibrarianTitle")}
      subtitle={selectedChatbot ? t("chattingWith", { name: selectedChatbot.name }) : t("noChatbotSelected")}
      handleNewUserMessage={handleNewUserMessage}
      launcherOpenImg={logo}
      toolbarSlot={headerToolbar}
    >
      <SpeechToTextButton onClick={toggleRecording} />
    </Widget>
  )
}
